import ApolloLinkExecutor from 'src/internal/revamp/apollo/ApolloLinkExecutor';
import {getCache} from 'src/internal/revamp/apollo/getCache';
import MemoryCache from 'src/internal/revamp/cache/MemoryCache';
import InteroperabilityLayer from 'src/internal/revamp/interoperability/InteroperabilityLayer';
import createApolloLink from 'src/internal/revamp/links/createApolloLink';
import ErrorCache from 'src/internal/revamp/observable/ErrorCache';
import ObservableStore from 'src/internal/revamp/observable/ObservableStore';
import ResultStore from 'src/internal/revamp/observable/ResultStore';
import PollingClock from 'src/internal/revamp/polling/PollingClock';
import {reportRuntimeError} from 'src/internal/revamp/telemetry/internalReporting';

import type {
  GraphQlConfig,
  GraphQlDataClient,
} from 'src/internal/revamp/config/types';
import type {Fetch} from 'src/internal/shared/fetch/types';
import type {ObservabilityFns} from 'src/internal/shared/observability/types';

export const MAX_STORE_SIZE = 10_000;

// Keep it as a module singleton to avoid creating a new clock for each DataClient.
const pollingClock = new PollingClock();

export default function createGraphQlDataClient(
  graphQlConfig: GraphQlConfig | undefined,
  fetchFunction: Fetch,
  observabilityFns: ObservabilityFns,
): GraphQlDataClient | undefined {
  if (!graphQlConfig) {
    return undefined;
  }

  try {
    const {staleTime, schemas} = graphQlConfig;
    const link = createApolloLink(schemas, observabilityFns);

    const errorCache = new ErrorCache();
    const memoryCache = new MemoryCache(
      observabilityFns,
      getCache(schemas, graphQlConfig.sharedCache, graphQlConfig.sharedCacheId),
    );
    const resultStore = new ResultStore(memoryCache, errorCache, staleTime);
    const apolloLinkExecutor = new ApolloLinkExecutor(link, fetchFunction);

    const observableStore = new ObservableStore(
      observabilityFns,
      apolloLinkExecutor,
      resultStore,
      memoryCache,
      pollingClock,
    );
    const interoperabilityLayer = new InteroperabilityLayer(resultStore);

    return {
      memoryCache,
      observableStore,
      resultStore,
      apolloLinkExecutor,
      interoperabilityLayer,
      pollingClock,
    };
  } catch (error) {
    // This'll ensure that errors in the revamp layer do not crash the entire application.
    // When revamp is rolled out, we'll re-throw the error, as it would be a catastrophic error warranting a full app breakage.
    reportRuntimeError(
      observabilityFns,
      error,
      'GraphQL client initialization',
    );
    return undefined;
  }
}
